import {
  getAppsByType, getApps, getAppsV3,
} from '@/modules/app/services/appsService';
import appService from '@/modules/app/services/appService';
import embeddedAppStore from '@/modules/app/store/embeddedAppStore';
import ANGULAR_EMBEDDED_APPS from './angularEmbeddedApps';

// Flags apps that render embedded inside POV (vs the Angular iframe).
const markMigratedApps = (apps, forceMigrated) => apps.map((item) => {
  if (forceMigrated || (item.app_code_name && !ANGULAR_EMBEDDED_APPS.includes(item.app_code_name))) {
    return { ...item, migrated: true };
  }

  return item;
});

// Apply special handling for specific apps (like the PayPal logo fix).
const PAYPAL_LOGO = 'https://app0.vcdnita.com/vue/img/Paypal_venmo_full_sized_with_fixed_O.bd4ebfdd.svg';
const applySpecialAppHandling = (apps) => apps.map((app) => (app.app_code_name === 'paypalwallet'
  ? { ...app, description: { ...app.description, logo: PAYPAL_LOGO } }
  : app));

// Dedup of the in-flight v3 fetch; kept out of Vuex state (a Promise shouldn't be reactive/serialized).
let inFlightAppsPromise = null;

const state = {
  // v3 single source of truth (VCITA2-14215)
  apps: [],
  loaded: false,
  // legacy v2 (removed once consumers migrate)
  availableApps: {
    ai_assistant: null,
  },
  marketApps: [],
  billingApp: undefined,
  availableMarketApps: [],
  externalApplications: [],
};

const getters = {
  // v3 single source (client-side slices of state.apps)
  allApps: (state) => state.apps,
  appsLoaded: (state) => state.loaded,
  appByCodeName: (state) => (appCodeName) => state.apps.find((app) => app?.app_code_name === appCodeName),
  installedApps: (state) => state.apps.filter((app) => app.status === 'installed'),
  // Step 2 (VCITA2-14215): replace the legacy `marketApps` getter (below) with this v3-derived slice
  // (import { MARKET_EXCLUDED_CODES } from '../constants/appConstants').
  // marketApps: (state) => state.apps.filter((app) => !app.app_types?.includes('billing_app')
  //   && !app.assignment_types?.includes('internal')
  //   && !MARKET_EXCLUDED_CODES.includes(app.app_code_name)),
  // Placeholder (v3 dark): returns the full catalog until step 2 reproduces get_external_applications
  // (installed + internal + billing) with requires_upgrade filtering for non-owners (gap §6).
  externalApps: (state) => state.apps,
  appsByType: (state) => (type) => state.apps.filter((app) => app.app_types?.includes(type)),
  // TODO (step 4 / gap §6.1): per-staff menu subitem permission filtering.
  menuApps: (state) => state.apps.filter((app) => (app.assignment_types?.includes('installed')
    || app.assignment_types?.includes('internal')) && app.menu_items),
  isAppInstalled: (state) => (appCodeName) => state.apps.some(
    (app) => app.app_code_name === appCodeName && app.status === 'installed',
  ),
  isAppAvailable: (state) => (appCodeName) => state.apps.some((app) => app.app_code_name === appCodeName),

  // legacy v2 (consumed today; removed once consumers migrate)
  availableApps: (state) => state.availableApps,
  availableAppByCodeName: (state) => ({ appCodeName }) => Object.values(state.availableApps)
    .flat()
    .find(
      (app) => app?.app_code_name === appCodeName,
    ),
  marketApps: (state) => state.marketApps,
  getMarketAppByName: (state) => (appCodeName) => state.marketApps.find((app) => app.app_code_name === appCodeName),
  billingApp: (state) => state.billingApp,
  availableMarketApps: (state) => state.availableMarketApps,
  getMarketAppById: (state) => (id) => state.availableMarketApps.find((app) => app.app_code_name === id),
  externalApplications: (state) => state.externalApplications,
  getExternalAppById: (state) => (id) => state.externalApplications.find((app) => app.app_code_name === id),
  purchasedApps: (state) => state.availableMarketApps.filter((app) => app.paid_data && !app.paid_data.requires_purchase),
};

const mutations = {
  setApps(state, apps) {
    state.apps = apps;
  },
  setLoaded(state, loaded) {
    state.loaded = loaded;
  },
  setAvailableApps(state, { type, availableApps }) {
    if (!Array.isArray(availableApps)) return;
    state.availableApps[type] = availableApps;
  },
  setMarketApps(state, { apps }) {
    state.marketApps = apps;
  },
  setBillingApp(state, app) {
    state.billingApp = app;
  },
  setAvailableMarketApps(state, apps) {
    state.availableMarketApps = apps;
  },
  setExternalApplications(state, apps) {
    state.externalApplications = apps;
  },
};

const actions = {
  async loadApps({ state, commit, rootGetters }, { force = false } = {}) {
    if (state.loaded && !force) return state.apps;
    if (inFlightAppsPromise && !force) return inFlightAppsPromise;

    const promise = (async () => {
      try {
        const apps = await getAppsV3();
        const forceEmbedded = rootGetters['BusinessStore/hasFeature']('force_embedded_apps_in_POV');
        commit('setApps', markMigratedApps(apps, forceEmbedded));
        commit('setLoaded', true);
        return state.apps;
      } catch (error) {
        console.error('Error loading apps', error);
        return [];
      } finally {
        // Only clear if a newer (e.g. forced) fetch hasn't already replaced this one.
        if (inFlightAppsPromise === promise) inFlightAppsPromise = null;
      }
    })();

    inFlightAppsPromise = promise;
    return promise;
  },

  refreshApps({ dispatch }) {
    return dispatch('loadApps', { force: true });
  },

  // legacy v2 (consumed today; removed once consumers migrate)
  async loadAppsByType({ rootGetters, commit, state }, type) {
    if (state.availableApps[type]) {
      return;
    }

    const businessUid = rootGetters['AuthStore/businessUid'];
    const res = await getAppsByType({ type, businessUid });

    if (res.apps) {
      commit('setAvailableApps', { type, availableApps: res.apps });
    }
  },
  // TODO (VCITA2-14215, step 2): `getMarketApps`/`marketApps` and `getAvailableMarketApps`/`availableMarketApps`
  // both fetch /v2/settings/get_available_market_apps — collapse both into the v3 `marketApps` getter.
  async getMarketApps({ commit }) {
    const res = await getApps();
    commit('setMarketApps', res);
  },
  async loadBillingAppData({ commit, state }, businessUid) {
    if (!state.billingApp) {
      const result = await appService.getAppsByType({ type: 'billing_app', businessUid });
      if (result.code !== 'error') {
        commit('setBillingApp', result.apps[0]);
      }
    }
  },
  async getAvailableMarketApps({
    commit, state, rootGetters, dispatch,
  }, params = {}) {
    const { force = false, hasPaidApps = false } = params;

    if (!state.availableMarketApps.length || force) {
      try {
        const result = await appService.getAvailableMarketApps();
        const apps = applySpecialAppHandling([...result.apps]);

        let aggregatedApps = apps;
        if (hasPaidApps) {
          const geoData = rootGetters['AccountStore/geoData'];
          aggregatedApps = await dispatch('SubscriptionStore/aggregateAppData', { apps, geoData }, { root: true });
        }

        const filteredApps = markMigratedApps(aggregatedApps, rootGetters['BusinessStore/hasFeature']('force_embedded_apps_in_POV'));
        commit('setAvailableMarketApps', filteredApps);
      } catch (error) {
        console.error('Failed to call getAvailableMarketApps', error);
      }
    }
  },
  async getExternalApplications({ commit, state, rootGetters }, force = false) {
    if (!state.externalApplications.length || force) {
      try {
        const result = await appService.getExternalApps();
        const filteredApps = markMigratedApps(result.apps, rootGetters['BusinessStore/hasFeature']('force_embedded_apps_in_POV'));
        commit('setExternalApplications', filteredApps);
      } catch (error) {
        console.error('Failed to call getExternalApps', error);
      }
    }
  },
};

const modules = {
  EmbeddedAppStore: embeddedAppStore,
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  modules,
};
